/**
 * Review the uncommitted changes and send one summary to the chief of staff.
 *
 * The optional note is persisted through `useDraft` and cleared only after a
 * successful commit. The last commit's job state and — once the chief of
 * staff answered — its `summary_md` render below the list.
 */
import { useState } from "react";
import { clsx } from "clsx";

import type { CommitRef, PendingChanges } from "../../api/focus";
import { CHANGE_KIND, chiefOfStaffName } from "../../lib/focus";
import { formatRelative } from "../../lib/formatRelative";
import { draftKey, useDraft } from "../../lib/hooks/useDraft";
import { Markdown } from "../Markdown";
import { Modal } from "../Modal";
import { Pill } from "../Pill";

export function CommitModal({
  open,
  pending,
  lastCommit,
  onCommit,
  onClose,
}: {
  open: boolean;
  pending: PendingChanges | undefined;
  lastCommit: CommitRef | null | undefined;
  /** Resolves on success, rejects (keeping the note) on failure. */
  onCommit: (note: string) => Promise<void>;
  onClose: () => void;
}) {
  const { draft, setDraft, clearDraft, restored } = useDraft(draftKey("focus-commit", "next"));
  const [sending, setSending] = useState(false);
  const name = chiefOfStaffName(pending);
  const items = pending?.items ?? [];
  const noCos = pending !== undefined && pending.chief_of_staff === null;

  async function send() {
    setSending(true);
    try {
      await onCommit(draft.trim());
      clearDraft();
    } catch {
      // The page toasts; the note stays for a retry.
    } finally {
      setSending(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      eyebrow="Commit"
      title={`Inform ${name}`}
      widthClass="max-w-2xl"
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className="btn btn-main"
            disabled={sending || items.length === 0}
            onClick={() => void send()}
          >
            {sending ? "Sending…" : `Inform ${name} – ${items.length} ${items.length === 1 ? "change" : "changes"}`}
          </button>
        </>
      }
    >
      {noCos ? (
        <div className="focus-banner mb-4">
          No chief of staff configured – committing marks these changes as done, but nobody is informed.
        </div>
      ) : null}

      {items.length === 0 ? (
        <p className="text-sm text-ink-low">Everything is committed.</p>
      ) : (
        <ul className="change-list">
          {items.map((item, i) => {
            const kind = CHANGE_KIND[item.kind] ?? { label: item.kind, tone: "muted" as const };
            return (
              <li key={`${item.entity_id ?? "ranking"}:${i}`} className="change-row">
                <Pill value={kind.label} tone={kind.tone} />
                <span className="change-label">{item.label}</span>
                {item.team_visible && !item.already_announced ? (
                  <span className="change-team" title="The AI team sees this goal">
                    team
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {items.length > 0 ? (
        <div className="mt-4">
          <label className="label" htmlFor="focus-commit-note">
            Note for {name} <span className="font-normal text-ink-low">(optional)</span>
          </label>
          <textarea
            id="focus-commit-note"
            className="input"
            rows={3}
            maxLength={1000}
            placeholder="Context the summary can't carry – why the priorities moved, what to watch."
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          {restored ? <p className="text-xs text-ink-low mt-1">Restored your unsent note.</p> : null}
        </div>
      ) : null}

      {lastCommit ? <LastCommit commit={lastCommit} /> : null}
    </Modal>
  );
}

const JOB_TONE: Record<string, "ok" | "signal" | "alert" | "muted"> = {
  submitted: "signal",
  working: "signal",
  parked: "signal",
  completed: "ok",
  failed: "alert",
  canceled: "muted",
  purged: "muted",
};

function LastCommit({ commit }: { commit: CommitRef }) {
  const summary = typeof commit.job?.result?.summary_md === "string" ? commit.job.result.summary_md : null;
  return (
    <section className="last-commit">
      <div className="last-commit-head">
        <span className="font-mono">last commit</span>
        <span>{commit.created_at ? formatRelative(commit.created_at) : ""}</span>
        <span>
          · {commit.change_count} {commit.change_count === 1 ? "change" : "changes"}
        </span>
        <span className={clsx(commit.outcome !== "informed" && "text-yellow-dark")}>
          ·{" "}
          {commit.outcome === "informed"
            ? `informed ${commit.informed?.name ?? commit.informed?.slug ?? "chief of staff"}`
            : commit.outcome === "no_cos"
              ? "no chief of staff – nothing sent"
              : "several chiefs of staff – nothing sent"}
        </span>
        {commit.job ? <Pill value={commit.job.status} tone={JOB_TONE[commit.job.status] ?? "muted"} /> : null}
      </div>
      {commit.job?.error ? <p className="text-xs text-yellow-dark mt-1">{commit.job.error}</p> : null}
      {summary ? (
        <div className="last-commit-summary">
          <Markdown text={summary} />
        </div>
      ) : commit.lines.length > 0 ? (
        <ul className="last-commit-lines">
          {commit.lines.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
